import { gql } from '@apollo/client';
import { BASE_DOCUMENT, BASE_FIELD } from '../../document/queries.ts';

const FULL_CHANNEL = gql`
    fragment FullChannel on UniversalProcessingChannelType {
        id
        name
        team {
            id
            name
        }
        emailAddress
        timePerDocumentBeforeWorkist

        emailConfig {
            enabled
            inboxName
            allowSentByMatchByName
            enableEmailOnlyDocumentProcessing
        }

        masterDataConfig {
            authToken

            clientLookupDefinitionId
            contactLookupDefinitionId
            clientInternalContactLookupDefinitionId
            emailLookupDefinitionId

            articleLookupDefinitionId
            debitorArticleLookupDefinitionId
            conversionFactorLookupDefinitionId
            frameworkContractLookupDefinitionId

            invoiceAddressLookupDefinitionId
            deliveryAddressLookupDefinitionId
            clientMappingLookupDefinitionId

            articleMappingLookupDefinitionId
            vatIdCheckMappingLookupDefinitionId

            invoiceAddressMappingLookupDefinitionId
            deliveryAddressMappingLookupDefinitionId
        }

        sftpConfig {
            sftpHost
            sftpHostKey
            sftpPort
            sftpUser
        }

        as2Config {
            senderAs2Name
            receiverUrl
            receiverAs2Name
            receiverVerifyCert
            receiverEncryptCert
            verifyReceiverCertificates
        }

        uploadConfig {
            enabled
            targetPathFileExport
            targetPathOriginalDocument
            sendOriginalDocument
            sendOriginalEmail
            deliveryMethod
        }

        documentConfig {
            options {
                name
                value
                valueType
                defaultValue
            }

            fields {
                name
                valueType
                position
                enabled
                options {
                    name
                    value
                    valueType
                    defaultValue
                    configurable
                }
            }
        }

        discardConfig {
            forwardingAddresses
            sendBack
        }
    }
`;

const FULL_UNIVERSAL = gql`
    fragment FullUniversal on UniversalDocument {
        ${BASE_DOCUMENT}
        customer {
            name
            customerNumber
            customerType
            misc
            contactPerson
            ${BASE_FIELD}
            address {
                addressId
                name
                postcode
                city
                country
                phone
                email
                companyName
                streetAndNr
                misc
                bbox
                pageIndex
                predictionConfidence
            }
            internalNote
        }
        
        lineItems {
            dynamicSchemaSerialized
        }
        
        dynamicSchemaSerialized
    }
`;

const FULL_RECORD = gql`
    ${FULL_UNIVERSAL}
    ${FULL_CHANNEL}
    fragment FullRecord on UniversalProcessingRecord {
        id
        createdAt
        deletedAt
        deletedBy {
            email
            firstName
            lastName
        }
        deletedReason
        assignedTo {
            id
            firstName
            lastName
            email
        }
        emailCount

        sender
        subject

        status

        canFinishAssistance
        canFinishHeaderAssistance
        canFinishLineItemsAssistance

        isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
        isLineItemsCorrect: canFinishLineItemsAssistance(ignoreThreshold: false)

        lockedBy {
            email
        }

        putOnHoldReason
        putOnHoldBy {
            email
            firstName
            lastName
        }

        assistedBy {
            email
            firstName
            lastName
        }

        assistedAt

        stepRun {
            id
            stepName
            manualOnly
            executionStatus
            error
            context
        }

        universalprocessingsteprunSet {
            id
            createdAt
            updatedAt
            stepName
            manualOnly
            executionStatus
            context
            error
        }

        channel {
            ...FullChannel
        }

        isDiscarded
        isTestDocument
        documentType

        discardRecord {
            reason
            user {
                id
                firstName
                lastName
            }
            createdAt
            updatedAt
        }

        sourceFile {
            ... on Email {
                sender {
                    email
                    name
                }
                receivers {
                    email
                    name
                }
                subject
                messageId
                language
                receivedAt

                text
                html
                attachments {
                    cid
                    contentDisposition
                    name
                    type
                    url
                    sourcePartId
                }

                senderClientId
                prediction
                predictionConfidence
                sourceUrl

                predictionConfidences
            }

            ... on File {
                name
                type
                url
            }
        }

        sourceParts {
            id
            fileName
            mimeType
            partType
            fileUrl
            classificationLabel
            classificationConfidence
            recordId
            classificationCorrectedBy
            classificationCorrectedAt
        }

        inputFile {
            ... on File {
                name
                type
                sourceType
            }
        }

        outputFile {
            ... on Export {
                files {
                    name
                    url
                }
            }
        }

        universalUnsaved {
            ...FullUniversal
        }

        externalReference
        deliveryRecord {
            deliveryMethod
            deliveryStatus
            externalReference
            externalMessage
            externalStatus
            externalUrl
        }
        labelingStatus

        masterdataVersionsExtracted
    }
`;

export const GET_OVERVIEW_DATA = gql`
    query {
        universalProcessingChannels {
            id
            name
        }
    }
`;

export const GET_CHANNELS_OVERVIEW_DATA = gql`
    query {
        universalProcessingChannels {
            id
            name
            emailAddress
            team {
                id
                users {
                    email
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_TABLE_OVERVIEW_DATA = gql`
    query ($filters: [TableFilter], $orderBy: String, $offset: Int, $first: Int) {
        filteredUniversalProcessingRecordsList(filters: $filters, orderBy: $orderBy, offset: $offset, limit: $first) {
            totalCount
            records {
                id
                sender
                status
                createdAt
                finishedAt
                assistedAt
                assistedBy {
                    id
                    firstName
                    lastName
                    email
                }
                createdBy {
                    email
                    firstName
                    lastName
                }
                assignedTo {
                    id
                    firstName
                    lastName
                    email
                }
                deletedAt
                deletedBy {
                    id
                    firstName
                    lastName
                    email
                }
                deletedReason
                lockedBy {
                    email
                }
                stepRun {
                    id
                    stepName
                    manualOnly
                    executionStatus
                    error
                    context
                }
                channel {
                    id
                    name
                }
                isDiscarded
                deliveryRecord {
                    deliveryMethod
                    deliveryStatus
                    externalReference
                    externalMessage
                    externalStatus
                    externalUrl
                }
                discardRecord {
                    reason
                    user {
                        id
                        firstName
                        lastName
                        email
                    }
                    createdAt
                    updatedAt
                }
                isTestDocument
                canProcessAutomatically
                title
                subtitle
                attachmentsCount
                sourcePart {
                    sender {
                        email
                        name
                    }
                }
                emailCount
                putOnHoldReason
                putOnHoldBy {
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const GET_ASSISTANCE_OVERVIEW_DATA = gql`
    query ($recordId: UUID!, $filters: [TableFilter]) {
        universalProcessingRecordAssistancePagination(recordId: $recordId, filters: $filters) {
            filteredCount
            position
            previousRecordId
            nextRecordId
        }
    }
`;

export const GET_ASSISTANCE_DATA = gql`
    ${FULL_RECORD}
    ${FULL_UNIVERSAL}
    query ($id: UUID!) {
        universalProcessingRecord(id: $id) {
            ...FullRecord

            universalExtracted {
                ...FullUniversal
            }

            dusDocument {
                pagesCount
                processedFileUrl
            }

            dusEmail {
                pagesCount
                processedFileUrl
            }
        }
    }
`;

export const UPDATE_PROCESSING_RECORD = gql`
    mutation (
        $recordId: UUID!
        $fieldName: String
        $itemIndex: Int
        $action: String!
        $payload: GenericScalar
        $itemType: String
    ) {
        universalUpdate(
            recordId: $recordId
            fieldName: $fieldName
            itemIndex: $itemIndex
            action: $action
            payload: $payload
            itemType: $itemType
        ) {
            record {
                canFinishAssistance
                canFinishHeaderAssistance
                canFinishLineItemsAssistance

                isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
                isLineItemsCorrect: canFinishLineItemsAssistance(ignoreThreshold: false)
                stepRun {
                    id
                    executionStatus
                    manualOnly
                    stepName
                }
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const RESELECT_PROCESSING_RECORD = gql`
    mutation (
        $recordId: UUID!
        $fieldName: String
        $bbox: [[Float]]!
        $pageIndex: Int!
        $itemIndex: Int
        $itemType: String
        $isEmailOcr: Boolean
    ) {
        universalReselect(
            recordId: $recordId
            fieldName: $fieldName
            bbox: $bbox
            pageIndex: $pageIndex
            itemIndex: $itemIndex
            itemType: $itemType
            isEmailOcr: $isEmailOcr
        ) {
            record {
                canFinishAssistance
                canFinishHeaderAssistance
                canFinishLineItemsAssistance

                isHeaderCorrect: canFinishHeaderAssistance(ignoreThreshold: false)
                isLineItemsCorrect: canFinishLineItemsAssistance(ignoreThreshold: false)
            }

            patch {
                op
                path
                from
                value
            }
        }
    }
`;

export const UNIVERSAL_EXTRACT_TEXT = gql`
    mutation ($recordId: UUID!, $pageIndex: Int!, $bbox: [[Float]]!, $isEmailOcr: Boolean = false) {
        universalExtractText(recordId: $recordId, pageIndex: $pageIndex, bbox: $bbox, isEmailOcr: $isEmailOcr) {
            bbox
            text
        }
    }
`;

export const RETRY_STEP = gql`
    mutation ($recordId: UUID!, $stepRunId: UUID!) {
        universalRetryStep(recordId: $recordId, stepRunId: $stepRunId) {
            success
        }
    }
`;

export const REOPEN_FOR_ASSISTANCE = gql`
    mutation ($recordId: UUID!, $reason: String) {
        universalReopenForAssistance(recordId: $recordId, reason: $reason) {
            success
        }
    }
`;

export const UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $batchId: String, $isTestingDocument: Boolean!) {
        universalUploadFile(
            input: { channelId: $channelId, files: $files, batchId: $batchId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const RE_UPLOAD_FILE = gql`
    mutation ($channelId: UUID!, $files: [Upload]!, $recordId: String!, $isTestingDocument: Boolean!) {
        universalUploadFile(
            input: { channelId: $channelId, files: $files, recordId: $recordId, isTestingDocument: $isTestingDocument }
        ) {
            records {
                id
            }
            success
        }
    }
`;

export const GET_NEXT_ASSISTANCE_RECORD = gql`
    query ($filters: [TableFilter], $channelId: UUID, $orderBy: String) {
        nextUniversalProcessingRecord(filters: $filters, channelId: $channelId, orderBy: $orderBy) {
            id
        }
    }
`;

export const CREATE_CHANNEL = gql`
    mutation ($name: String!, $fromExistingChannelId: UUID) {
        createUniversalProcessingChannel(name: $name, fromExistingChannelId: $fromExistingChannelId) {
            channel {
                id
            }
            success
        }
    }
`;

export const GET_ALL_CHANNELS = gql`
    ${FULL_CHANNEL}
    query {
        universalProcessingChannels {
            ...FullChannel
        }
    }
`;

export const SEND_TO_LABELING = gql`
    mutation ($recordId: UUID!) {
        universalSendToLabeling(recordId: $recordId) {
            success
        }
    }
`;

export const UPDATE_FIELDS_CONFIG = gql`
    mutation UpdateUniversalDocumentConfig(
        $channelId: UUID!
        $documentType: String!
        $documentConfig: DocumentConfigInput
        $fieldConfigs: [FieldConfigInput]
    ) {
        updateUniversalDocumentConfig(
            channelId: $channelId
            documentType: $documentType
            documentConfig: $documentConfig
            fieldConfigs: $fieldConfigs
        ) {
            success
        }
    }
`;

export const GET_CHANNEL_CONFIG = gql`
    ${FULL_CHANNEL}
    query ($id: UUID!) {
        universalProcessingChannel(id: $id) {
            ...FullChannel
        }
    }
`;

export const CREATE_CONFIG = gql`
    mutation ($channelId: UUID!, $configName: String!) {
        createUniversalProcessingChannelConfig(channelId: $channelId, configName: $configName) {
            success
        }
    }
`;

export const DELETE_CONFIG = gql`
    mutation ($channelId: UUID!, $configName: String!) {
        deleteUniversalProcessingChannelConfig(channelId: $channelId, configName: $configName) {
            success
        }
    }
`;

export const UPDATE_CHANNEL = gql`
    mutation (
        $channelId: UUID!
        $name: String
        $teamId: UUID
        $emailConfig: UpdateEmailConfigInput
        $masterDataConfig: UpdateMasterDataConfigInput
        $xmlSerializerConfig: UpdateXMLSerializerConfigInput
        $sftpConfig: UpdateSFTPConfigInput
        $as2Config: UpdateAS2ConfigInput
        $uploadConfig: UpdateUploadConfigInput
        $discardConfig: UpdateDiscardConfigInput
        $timePerDocumentBeforeWorkist: Int
    ) {
        updateUniversalProcessingChannel(
            channelId: $channelId
            name: $name
            teamId: $teamId
            emailConfig: $emailConfig
            masterDataConfig: $masterDataConfig
            xmlSerializerConfig: $xmlSerializerConfig
            sftpConfig: $sftpConfig
            as2Config: $as2Config
            uploadConfig: $uploadConfig
            discardConfig: $discardConfig
            timePerDocumentBeforeWorkist: $timePerDocumentBeforeWorkist
        ) {
            success
        }
    }
`;

export const GET_RELATED_DOCUMENT = gql`
    query ($id: UUID!) {
        universalProcessingRecord(id: $id) {
            id
            subject
            createdAt
            finishedAt
            assistedAt
            assistedBy {
                id
                firstName
                lastName
                email
            }
            status
        }
    }
`;
